/**
 * Modo celular (docs/spec/mobile.md): o dedo como ponteiro principal, com a tela estreita (o celular
 * em pé) ou com pouca altura (o celular deitado). O tablet fica com o layout de painéis, com o toque
 * consertado, e a janela estreita do computador (uma janela destacada, o navegador encolhido) também:
 * não é um celular.
 *
 * - `useMobile()`: os componentes que mudam de forma no celular.
 * - A classe `rd-mobile` no `<html>`, com a mesma condição: o CSS do celular não se desencontra do
 *   React.
 * - A altura que o teclado virtual deixa à vista (`--vv-height`, `--vv-top`): no iPhone o teclado
 *   não encolhe a página, e a caixa do chat ficaria atrás dele. Com zoom de pinça na página, a
 *   altura não vale (a mesa encolheria junto).
 */
import { useCallback, useSyncExternalStore } from 'react';

export const MOBILE_QUERY = '(pointer: coarse) and (max-width: 760px), (pointer: coarse) and (max-height: 500px)';

const query = (): MediaQueryList | null => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(MOBILE_QUERY) : null);

/** Está no modo celular agora? */
export function isMobileNow(): boolean {
  return query()?.matches ?? false;
}

function subscribe(onChange: () => void): () => void {
  const mq = query();
  if (!mq) return () => undefined;
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

/** O modo celular, acompanhando a rotação e o redimensionamento. */
export function useMobile(): boolean {
  return useSyncExternalStore(subscribe, isMobileNow, () => false);
}

/** A tela estreita, com dedo ou sem: o Início e o Explorar põem as mesas em linhas. */
export const NARROW_QUERY = '(max-width: 760px)';

/** Uma media query do CSS, acompanhando a janela. */
export function useMediaQuery(media: string): boolean {
  const subscribeTo = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => undefined;
      const mq = window.matchMedia(media);
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    },
    [media],
  );
  return useSyncExternalStore(subscribeTo, () => (typeof window.matchMedia === 'function' ? window.matchMedia(media).matches : false), () => false);
}

/** Liga a classe do `<html>` e a altura à vista (o `main.tsx` chama uma vez, antes de desenhar). */
export function installMobileMode(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const mark = () => root.classList.toggle('rd-mobile', isMobileNow());
  mark();
  subscribe(mark);

  const vv = window.visualViewport;
  if (!vv) return;
  const fit = () => {
    // zoom de pinça na página: a altura à vista é a do zoom, não a do teclado
    if (Math.abs(vv.scale - 1) > 0.01) {
      root.style.removeProperty('--vv-height');
      root.style.removeProperty('--vv-top');
      return;
    }
    root.style.setProperty('--vv-height', `${Math.round(vv.height)}px`);
    root.style.setProperty('--vv-top', `${Math.round(vv.offsetTop)}px`);
  };
  fit();
  vv.addEventListener('resize', fit);
  vv.addEventListener('scroll', fit);
}
